import {
  Box,
  Flex,
  type FlexProps,
  Heading,
  Spacer,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { DateTime } from 'luxon'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { withOrdinalSuffix } from '@/utils/helpers'
import { DATE_WITH_WEEKDAY_FORMAT, TIME_WITH_TIMEZONE_SHORT_FORMAT } from '@/constants/date'

import { SAT_BOOTCAMP_READING_COURSE } from '@/queries/constants'
import getPlainText from '@/client/services/richText/getPlainText'
import { SAT_SCORE_BAND_MESSAGE } from '@/client/services/satBootcamp'
import { seriesTimeInfoToString } from '@/client/services/series'
import { getSeriesUrlById } from '@/client/services/url'

import HostAvatars from '@/components/Host/HostAvatars'
import { toRegistrationState } from '@/components/RegistrationState/helpers'
import RegistrationState from '@/components/RegistrationState/RegistrationState'
import LiveNowTag from '@/components/UI/LiveNowTag/LiveNowTag'
import OptionalSoftLink from '@/components/UI/OptionalSoftLink'
import RelativeTime from '@/components/UI/RelativeTime/RelativeTime'
import Tippy from '@/components/UI/Tippy'

import type { SeriesBoxProps } from './types'

const SeriesBox: React.FC<
  SeriesBoxProps & {
    noMaxLearners?: boolean
    disableWithTooltip?: string
    grayIfFull?: boolean
    variant?: 'gray' | 'primary'
    showRecurringTimes?: boolean
    hideSeriesHeader?: boolean
    openLinksInNewTab?: boolean
    disableLink?: boolean
    hideDescription?: boolean
    borderColor?: FlexProps['borderColor'] | null
  }
> = ({
  id,
  title,
  description,
  sessionCount,
  courseName,
  satSeriesId,
  satTestTitle,
  satScoreBandName,
  timeInfo,
  isUserRegistered,
  isUserSelfReserved,
  isEnrollmentClosed,
  nextSessionStart,
  nextSessionEnd,
  hasEnded,
  endedAtDateTime,
  hosts,
  numHosts,
  pastSessionsCount,
  registrationCount,
  reservationCount,
  maxLearners,
  disableWithTooltip,
  noMaxLearners = false,
  grayIfFull = false,
  variant = 'gray',
  showRecurringTimes = false,
  hideSeriesHeader: _ = false,
  openLinksInNewTab = false,
  disableLink = false,
  hideDescription = false,
  borderColor = null,
}) => {
  const isSATSeries = satSeriesId != null
  const satCourseName = courseName === SAT_BOOTCAMP_READING_COURSE ? 'Reading and Writing' : 'Math'
  const satSeriesTitle = `${satCourseName} for the ${satTestTitle}`

  const currentDateTime = DateTime.local()
  const isNextSessionLive = nextSessionStart < currentDateTime && currentDateTime < nextSessionEnd

  const endedAtDateString = endedAtDateTime.toLocaleString({
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const registrationAndReservationCount = registrationCount + reservationCount
  const sessionNumberText = `Session ${pastSessionsCount + 1} of ${sessionCount}`

  const nextSessionDateString = nextSessionStart.toLocaleString(DATE_WITH_WEEKDAY_FORMAT)
  const nextSessionStartTimeString = nextSessionStart.toLocaleString(DateTime.TIME_SIMPLE)
  const nextSessionEndTimeString = nextSessionEnd.toLocaleString(TIME_WITH_TIMEZONE_SHORT_FORMAT)

  const colors = {
    gray: {
      base: 'gray.muted',
      outline: 'gray.emphasized',
    },
    primary: {
      base: 'primary.emphasized',
      outline: 'gray.emphasized',
    },
  }

  const minHeightAddend = satScoreBandName == null ? 0 : 20

  return (
    <Tippy content={disableWithTooltip ?? ''} disabled={disableWithTooltip == null}>
      {/* Extra Box is there so that disabling pointerEvents on the box below still allows us to render a tooltip */}
      <Box>
        <Box style={disableWithTooltip != null ? { pointerEvents: 'none' } : {}}>
          <OptionalSoftLink
            href={disableLink ? null : getSeriesUrlById(id)}
            isExternal={openLinksInNewTab}
          >
            <Flex
              minHeight={`${(hideDescription ? 200 : 295) + minHeightAddend}px`}
              position="relative"
              direction="column"
              mx="2px"
              borderRadius="8px"
              border={`2px solid ${chakraTokenToVar(borderColor?.toString() ?? colors[variant].outline)}`}
              cursor="pointer"
              _hover={{
                bgColor: 'bg.subtle',
                border: `2px solid ${chakraTokenToVar('primary.focusRing')}`,
              }}
              bg="bg"
              opacity={
                (grayIfFull && maxLearners === registrationAndReservationCount) ||
                disableWithTooltip != null
                  ? 0.7
                  : 1
              }
            >
              <Stack
                borderTopRadius="6px"
                borderTop={`10px solid ${chakraTokenToVar(colors[variant].base)}`}
                padding="24px"
                direction="column"
                spacing="8px"
                justify="space-between"
                height="100%"
              >
                <Flex
                  minHeight={hideDescription ? 'unset' : `${85 + minHeightAddend}px`}
                  flexDir="column"
                >
                  {satScoreBandName != null ? (
                    <Box mb="6px">
                      <Tippy content={SAT_SCORE_BAND_MESSAGE}>
                        <Text variant="eyebrow" fontWeight="bold">
                          score range · {satScoreBandName}
                        </Text>
                      </Tippy>
                    </Box>
                  ) : null}

                  <Heading size="xl" as="h3" mb="2px" pb="2px" noOfLines={2} fontSize="20px">
                    {isSATSeries ? satSeriesTitle : title}
                  </Heading>

                  <Wrap alignItems="center">
                    {isNextSessionLive ? (
                      <WrapItem>
                        <LiveNowTag />
                      </WrapItem>
                    ) : null}
                    {showRecurringTimes ? (
                      <Tippy
                        content={
                          <div style={{ textAlign: 'center' }}>
                            {seriesTimeInfoToString({
                              timeInfo,
                              includeTimezone: true,
                            })}
                          </div>
                        }
                      >
                        <Text as="span" color="fg.muted" fontWeight="bold">
                          {seriesTimeInfoToString({ timeInfo })}
                        </Text>
                      </Tippy>
                    ) : hasEnded ? (
                      <Text as="span" color="fg.muted" fontWeight="bold">
                        Ended {endedAtDateString}
                      </Text>
                    ) : (
                      <Tippy
                        content={
                          <div style={{ textAlign: 'center' }}>
                            {sessionNumberText}
                            <br />
                            {nextSessionDateString}
                            <br />
                            {`${nextSessionStartTimeString} - ${nextSessionEndTimeString} `}
                          </div>
                        }
                      >
                        <Text as="span" color="fg.muted" fontWeight="bold">
                          {isNextSessionLive
                            ? 'Started '
                            : pastSessionsCount >= 1
                              ? `${withOrdinalSuffix(pastSessionsCount + 1)} session `
                              : 'Starts '}
                          <RelativeTime time={nextSessionStart.toISO()} includeWeekday />
                        </Text>
                      </Tippy>
                    )}
                  </Wrap>
                </Flex>
                {!hideDescription ? <Text noOfLines={3}>{getPlainText(description)}</Text> : null}
              </Stack>
              {hideDescription ? null : <Spacer />}
              <Box px="24px" py="16px" borderTop="1px solid" borderColor="border">
                <Stack direction="row" spacing={2} justify="space-between" align="center">
                  <HostAvatars
                    hosts={hosts}
                    numHosts={numHosts}
                    isSession={false}
                    isTooltip
                    size="sm"
                  />
                  <RegistrationState
                    state={toRegistrationState({
                      hasEnded,
                      isUserRegistered,
                      isEnrollmentClosed,
                      isUserSelfReserved,
                    })}
                    numRegistrations={registrationAndReservationCount}
                    maxLearners={maxLearners}
                    noMaxLearners={noMaxLearners}
                    isSeries
                  />
                </Stack>
              </Box>
            </Flex>
          </OptionalSoftLink>
        </Box>
      </Box>
    </Tippy>
  )
}

export default SeriesBox
